import type { ComponentProps } from 'react';

import mark from '../../assets/logo/mark.png';
import wordmarkDark from '../../assets/logo/wordmark-dark.png';
import wordmarkLight from '../../assets/logo/wordmark-light.png';
import { cn } from '../../lib/utils';
import { assetUrl } from './asset-url';

export type LogoVariant = 'wordmark-light' | 'wordmark-dark' | 'mark';

export type LogoProps = Omit<ComponentProps<'img'>, 'src'> & {
  /**
   * Figma "Variant". `wordmark-light` on ivory/white surfaces (default),
   * `wordmark-dark` on Forest Ink or black, `mark` for app icon / favicon /
   * collapsed sidebar.
   */
  variant?: LogoVariant;
  className?: string;
};

const SOURCES = { 'wordmark-light': wordmarkLight, 'wordmark-dark': wordmarkDark, mark };

/**
 * Reelplicate logo (Figma: ↳ Brand ↳ Logo, 70:7). Brand-file image fills -
 * never recoloured or redrawn. Wordmarks render at the Figma 120×48, the mark
 * at 48×48 with its 10px (≈20.8%) corner radius. Override `width` / `height`
 * (or size classes) to scale proportionally.
 */
export function Logo({ variant = 'wordmark-light', className, ...props }: LogoProps) {
  const isMark = variant === 'mark';
  return (
    <img
      data-slot="logo"
      data-variant={variant}
      src={assetUrl(SOURCES[variant])}
      alt="Reelplicate"
      width={isMark ? 48 : 120}
      height={48}
      decoding="async"
      className={cn('shrink-0 object-contain', isMark && 'rounded-[20.833%]', className)}
      {...props}
    />
  );
}
